// Список бесед (M6.1, M6.4).
//
// Сессии — те же, что на десктопе: appStore.sessions, тот же сервер, те же id.
// Последней реплики в SessionInfo нет, поэтому берём её из logStore, если
// беседа уже открывалась в этом запуске, а для остальных дочитываем хвост
// лога тем же api.log, которым пользуется панель. Запрос лёгкий (последние
// TAIL строк) и делается один раз на открытие вкладки.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import type { Line, SessionInfo, SessionState } from '../protocol'
import { useAppStore } from '../store/appStore'
import { useLogStore } from '../store/logStore'
import './chat.css'

/** Сколько строк хвоста читаем ради одной подписи «последняя реплика». */
const TAIL = 12
/** Больше этого числа бесед за раз не опрашиваем: список всё равно длиннее
 *  экрана, а сеть на телефоне дорогая. */
const PREVIEW_LIMIT = 20
/** Длина подписи под названием беседы. */
const CUT = 90

/** M6.1: «работает / ждёт». Завершённую беседу тоже называем честно —
 *  иначе непонятно, почему она не отвечает. */
const STATE_TAG: Record<SessionState, string> = {
  idle: 'ждёт',
  running: 'работает',
  waiting_permission: 'ждёт разрешения',
  exited: 'завершена',
}

/** Строки, которые не годятся в подпись: вывод инструментов и служебное. */
function isReply(kind: Line['kind']): boolean {
  return kind === 'me' || kind === 'agent' || kind === 'ok' || kind === 'warn'
}

function lastReply(lines: readonly Line[]): string {
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]
    if (!isReply(line.kind)) continue
    const text = (line.text ?? '').replace(/\s+/g, ' ').trim()
    if (!text) continue
    const prefix = line.kind === 'me' ? 'вы: ' : ''
    const full = `${prefix}${text}`
    return full.length > CUT ? `${full.slice(0, CUT - 1)}…` : full
  }
  return ''
}

interface Props { onOpen(sessionId: string): void }

export default function ChatsScreen({ onOpen }: Props) {
  const all = useAppStore((s) => s.sessions)
  const creating = useAppStore((s) => s.creating)
  const refreshSessions = useAppStore((s) => s.refreshSessions)
  const newSession = useAppStore((s) => s.newSession)
  // Срез лога нужен целиком: подпись должна оживать прямо в списке, когда
  // агент дописывает ответ в открытой раньше беседе.
  const slices = useLogStore((s) => s.sessions)

  const [tails, setTails] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void refreshSessions()
  }, [refreshSessions])

  // Только обычные беседы: закреплённый канал «Телеграм» живёт в своём корне
  // таб-бара. В общем списке он был бы ловушкой — выглядит как беседа, но его
  // нельзя ни создать, ни закрыть, и второго такого не бывает.
  //
  // Фильтруем здесь, а не селектором стора: селектор возвращал бы новый массив
  // на каждый рендер, и zustand считал бы срез изменившимся всегда.
  // Мозг голосового оркестратора — тоже беседа: Степан открывает её и видит,
  // что мозг делал.
  const sessions = useMemo(() => all.filter((s) => s.role === 'chat' || s.role === 'orchestrator' || s.role === 'orchestrator_planner'), [all])

  const ids = useMemo(() => sessions.map((s) => s.id), [sessions])
  const idsKey = ids.join(',')

  // Хвосты дочитываем только для тех бесед, которых ещё нет ни в logStore,
  // ни в уже полученных подписях: повторный вход на вкладку не должен
  // устраивать сети веер запросов.
  useEffect(() => {
    let dead = false
    const store = useLogStore.getState().sessions
    const want = ids
      .filter((id) => !(store[id]?.lines.length) && tails[id] === undefined)
      .slice(0, PREVIEW_LIMIT)
    if (want.length === 0) return
    void Promise.all(want.map(async (id) => {
      try {
        const page = await api.log(id, 0, TAIL)
        return [id, lastReply(page.lines)] as const
      } catch {
        // Молча: подпись — украшение списка, ради неё не стоит показывать
        // красную плашку поверх работающих бесед.
        return [id, ''] as const
      }
    })).then((pairs) => {
      if (dead) return
      setTails((prev) => {
        const next = { ...prev }
        for (const [id, text] of pairs) next[id] = text
        return next
      })
    })
    return () => { dead = true }
    // tails намеренно не в зависимостях: он же и меняется этим эффектом.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey])

  const create = useCallback(async () => {
    if (creating) return
    setError(null)
    const before = new Set(useAppStore.getState().sessions.map((s) => s.id))
    await newSession()
    const state = useAppStore.getState()
    const born = state.sessions.find((s) => !before.has(s.id))
    if (born) onOpen(born.id)
    else setError(state.notice ?? 'не удалось создать беседу')
  }, [creating, newSession, onOpen])

  // Свежие — сверху: на телефоне список листают, а не разглядывают целиком.
  const ordered = useMemo(
    () => [...sessions].sort((a, b) => b.lastActivity - a.lastActivity),
    [sessions],
  )

  const preview = (s: SessionInfo): string => {
    const lines = slices[s.id]?.lines
    if (lines && lines.length > 0) return lastReply(lines)
    return tails[s.id] ?? ''
  }

  return (
    <div className="ab-mchats">
      {error ? <div className="ab-mchats__fail">{error}</div> : null}

      {ordered.length === 0 ? (
        <div className="ab-mchats__empty">
          Бесед пока нет. Начните первую — агент ответит здесь же.
        </div>
      ) : null}

      {ordered.map((s) => (
        <button
          key={s.id}
          type="button"
          className="ab-mchats__row"
          onClick={() => onOpen(s.id)}
        >
          <span className={`ab-mchats__dot ab-mchats__dot--${s.state}`} aria-hidden />
          <span className="ab-mchats__text">
            <span className="ab-mchats__title">{s.title || s.id.slice(0, 8)}</span>
            <span className="ab-mchats__last">
              {preview(s) || (s.notePath ? `в базе: ${s.notePath}` : 'реплик пока нет')}
            </span>
          </span>
          <span className={`ab-mchats__tag ab-mchats__tag--${s.state}`}>
            {STATE_TAG[s.state]}
          </span>
        </button>
      ))}

      <button
        type="button"
        className="ab-mchats__new"
        disabled={creating}
        onClick={() => { void create() }}
      >
        {creating ? 'создаю…' : '+ новая беседа'}
      </button>
    </div>
  )
}
